import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { AuthenticationService, AuthenticatedIdentity, CurrentUser, Option } from 'app/domain';
import { WorkflowService } from '../../../data-access/workflow.service';
import { countActiveTasks } from '../landing/apr-landing-utils';

interface NavLink {
  url: string;
  label: string;
  iconClass: string;
  badgeCount?: number;
  // Explicit highlight override honoured by section-header. Left undefined for routes
  // that map cleanly to a tab (section-header's routerLinkActive drives those); set only
  // on the tab-less detail routes so the originating tab stays lit. See updateActiveTab.
  active?: boolean;
}

// Detail routes that belong to no tab. When the origin tab is unknown (cold deep-link or
// page refresh), highlight the tab the route naturally belongs to.
const DETAIL_FALLBACK_TAB: { prefix: string, tab: string }[] = [
  { prefix: '/review/task/', tab: 'my-tasks' },
  { prefix: '/review/packet/', tab: 'my-cases' },
];

@Component({
  selector: 'reviewer-dashboard',
  templateUrl: './reviewer-dashboard.component.html',
  styleUrls: ['./reviewer-dashboard.component.scss']
})
export class ReviewerDashboardComponent implements OnInit, OnDestroy {

  navLinks: NavLink[] = [
    { url: 'my-review', label: 'Reviewer Dashboard', iconClass: '' },
    { url: 'my-cases', label: 'My Cases', iconClass: '' },
    { url: 'my-tasks', label: 'My Tasks', iconClass: '' }
  ];

  private navSub?: Subscription;

  // The last real tab the user was on, used as the highlight "origin" when they drill into
  // a tab-less detail route (task/:taskId, packet/:caseId). The tab you came from already
  // encodes the context — My Cases for your own candidate, My Review when reviewing, etc.
  private lastTabUrl?: string;

  constructor(
    authSvc: AuthenticationService,
    private workflowSvc: WorkflowService,
    private router: Router
  ) {
    authSvc.currentIdentity.subscribe(identity => {
      identity.match(
        () => {},
        (i: AuthenticatedIdentity) => {
          if (i.user.roles.academicHumanResources) {
            if (!this.navLinks.some(l => l.url === 'all-cases')) {
              this.navLinks.push({ url: 'all-cases', label: 'All Cases', iconClass: '' });
            }
          }
        });
    });
  }

  ngOnInit(): void {
    // Load once up front. The NavigationEnd that activated this component is not
    // caught by the subscription below — it has already been emitted by the time
    // ngOnInit subscribes (e.g. landing directly on /review from a blank tab, or
    // returning to the dashboard after navigating away), so without this explicit
    // call the badge would be blank until the next in-app navigation.
    this.loadBadgeCount();
    // Same ngOnInit-timing reason: the activating NavigationEnd has already fired, so
    // resolve the active tab for the current URL explicitly here.
    this.updateActiveTab(this.router.url);

    // No task SignalR event exists yet, so refresh the count on subsequent in-app
    // navigation within the dashboard (switching tabs, returning from a completed
    // task). Collapse to the top-level tab ('/review/<tab>') and distinctUntilChanged
    // so sub-route changes within a tab (e.g. opening a case detail) don't refetch.
    this.navSub = this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(e => e.urlAfterRedirects.split('?')[0]),
        filter(url => url.startsWith('/review')),
        map(url => url.split('/').slice(0, 3).join('/')),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadBadgeCount());

    // Active-tab tracking runs off the raw (uncollapsed) URL so it can see the detail
    // routes the badge stream deliberately folds away.
    this.navSub.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(e => this.updateActiveTab(e.urlAfterRedirects)));
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
  }

  /// Resolve which dashboard tab should be highlighted for the given URL and write the
  /// result onto navLinks[].active. Tab routes (and their detail children, e.g.
  /// my-review/:caseId) clear the override and let section-header's routerLinkActive
  /// drive; tab-less detail routes pin the originating tab (or a route-based fallback).
  private updateActiveTab(rawUrl: string): void {
    const current = rawUrl.split('?')[0];
    const matchedTab = this.navLinks
      .map(l => l.url)
      .find(u => current === `/review/${u}` || current.startsWith(`/review/${u}/`));

    if (matchedTab) {
      this.lastTabUrl = matchedTab;
      this.setActiveTab(undefined);
    } else if (DETAIL_FALLBACK_TAB.some(d => current.startsWith(d.prefix))) {
      const fallback = DETAIL_FALLBACK_TAB.find(d => current.startsWith(d.prefix))!.tab;
      this.setActiveTab(this.lastTabUrl ?? fallback);
    } else {
      // Landing/home or anything outside the tabbed area — no tab is the origin.
      this.lastTabUrl = undefined;
      this.setActiveTab(undefined);
    }
  }

  /// undefined clears all overrides (routerLinkActive resumes control); otherwise the
  /// single matching tab is forced active and the rest forced inactive.
  private setActiveTab(activeTab: string | undefined): void {
    for (const link of this.navLinks) {
      link.active = activeTab === undefined ? undefined : link.url === activeTab;
    }
  }

  async loadBadgeCount(): Promise<void> {
    try {
      const tasks = await this.workflowSvc.getMyTasks();
      const count = countActiveTasks(tasks);
      const myTasks = this.navLinks.find(l => l.url === 'my-tasks');
      if (myTasks) {
        myTasks.badgeCount = count;
      }
    } catch (err) {
      console.error('Failed to load My Tasks badge count', err);
    }
  }
}
